import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import DynamicBadge from "../Common/DynamicBadge";
import { FaPencilAlt, FaRegTrashAlt, FaEye, FaBox, FaLayerGroup, FaSitemap, FaUsers, FaCloudUploadAlt } from "react-icons/fa";
import {
  DatatableWrapper,
  TableHeader,
  TableBody,
  Pagination,
  PaginationOptions,
  Filter,
} from "react-bs-datatable";
import "bootstrap/dist/css/bootstrap.min.css";
import { Button, Badge, Form, Spinner } from "react-bootstrap";
import {
  fetchProducts,
  deleteProduct,
  clearError,
  clearSuccess,
} from "../../redux/slices/productSlice";
import { fetchCategories } from "../../redux/slices/categorySlice";
import InfoPill from "../Common/InfoPill";
import ConfirmModal from "../Common/ConfirmModal";
import { toast } from "react-toastify";
import subCategoryService from "../../services/subCategoryService";
import userAdminService from "../../services/userAdminService";
import BulkUploadModal from "./BulkUploadModal";

const Products = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    products = [],
    isLoading,
    error,
    success,
    isDeleting,
  } = useSelector((state) => state.products);

  const { categories = [] } = useSelector((state) => state.categories);
  const { user } = useSelector((state) => state.auth);

  const [selectedProducts, setSelectedProducts] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [subCategoryFilter, setSubCategoryFilter] = useState("");
  const [vendorFilter, setVendorFilter] = useState("");
  const [subCategories, setSubCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const initialLoadDoneRef = useRef(false);

  useEffect(() => {
    dispatch(fetchCategories());
    // Clear any existing success message when component mounts
    dispatch(clearSuccess());

    // Fetch vendors if super admin or admin
    if (user?.user_type === "super_admin" || user?.user_type === "admin") {
      userAdminService.list({ user_type: "vendor", status: "Active" }).then((res) => {
        setVendors(res.data?.users || res.users || res.data || []);
      });
    }
  }, [dispatch, user]);

  // Fetch sub-categories when category changes
  useEffect(() => {
    if (categoryFilter) {
      subCategoryService
        .getSubCategoriesByCategoryId(categoryFilter)
        .then((res) => {
          const data = res.subCategories || res.data || res;
          setSubCategories(Array.isArray(data) ? data : []);
        });
    } else {
      setSubCategories([]);
    }
    setSubCategoryFilter(""); // Reset sub-category when category changes
  }, [categoryFilter]);

  useEffect(() => {
    const params = {};
    if (categoryFilter) params.category_id = categoryFilter;
    if (subCategoryFilter) params.sub_category_id = subCategoryFilter;
    if (vendorFilter) params.vendor_id = vendorFilter;

    const isFirstLoad = !initialLoadDoneRef.current;

    // Only set initial load state on first load
    if (isFirstLoad) {
      setIsInitialLoad(true);
    }

    dispatch(fetchProducts(params)).finally(() => {
      if (isFirstLoad) {
        setIsInitialLoad(false);
        initialLoadDoneRef.current = true;
      }
    });
  }, [dispatch, categoryFilter, subCategoryFilter, vendorFilter]);

  // Show toast messages for success and error
  useEffect(() => {
    if (error) {
      toast.error(error);
      const timer = setTimeout(() => dispatch(clearError()), 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (success) {
      toast.success(success);
      const timer = setTimeout(() => dispatch(clearSuccess()), 5000);
      return () => clearTimeout(timer);
    }
  }, [success, dispatch]);

  // Cleanup effect to clear success message when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearSuccess());
    };
  }, [dispatch]);

  const handleCreateProduct = () => {
    navigate("/product/create");
  };

  const handleEditProduct = (productId) => {
    navigate(`/product/edit/${productId}`);
  };

  const handleViewProduct = (productId) => {
    navigate(`/product/details/${productId}`);
  };

  const handleDeleteProduct = (productId, productName) => {
    setProductToDelete({ id: productId, name: productName });
    setShowConfirmModal(true);
  };

  const confirmDeleteProduct = async () => {
    if (productToDelete) {
      if (productToDelete.isBulk) {
        // Handle bulk delete
        try {
          // Delete all selected products
          for (const productId of productToDelete.ids) {
            await dispatch(deleteProduct(productId)).unwrap();
          }
          // Redux already updates the products array, no need to refetch
          // Only refetch if we need to sync with server (e.g., for pagination)
          setSelectedProducts([]);
          setShowConfirmModal(false);
          setProductToDelete(null);
        } catch (error) {
          console.error("Failed to delete products:", error);
          // Refetch on error to ensure state is in sync
          const params = categoryFilter ? { category: categoryFilter } : {};
          dispatch(fetchProducts(params));
          setSelectedProducts([]);
          setShowConfirmModal(false);
          setProductToDelete(null);
        }
      } else {
        // Handle single delete
        try {
          await dispatch(deleteProduct(productToDelete.id)).unwrap();
          // Redux already removes the product from the array, no need to refetch
          // Only refetch if we need to sync with server (e.g., for pagination)
          setShowConfirmModal(false);
          setProductToDelete(null);
          // Toast will be shown by useEffect based on Redux success state
        } catch (error) {
          console.error("Failed to delete product:", error);
          // Refetch on error to ensure state is in sync
          const params = categoryFilter ? { category: categoryFilter } : {};
          dispatch(fetchProducts(params));
          // Toast will be shown by useEffect based on Redux error state
          // Keep the modal open so user can see the error
        }
      }
    }
  };

  const cancelDeleteProduct = () => {
    setShowConfirmModal(false);
    setProductToDelete(null);
  };

  const handleBulkDelete = () => {
    if (selectedProducts.length === 0) {
      alert("Please select products to delete");
      return;
    }
    setProductToDelete({
      id: null,
      name: `${selectedProducts.length} products`,
      isBulk: true,
      ids: selectedProducts,
    });
    setShowConfirmModal(true);
  };

  const handleSelectProduct = (productId) => {
    setSelectedProducts((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
  };

  const handleSelectAll = () => {
    if (selectedProducts.length === products.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(products.map((product) => product.id));
    }
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find((cat) => cat.id === categoryId);
    return category ? category.name : categoryId || "No Category";
  };

  const selectedCategory = categories.find(c => c.id === categoryFilter);
  const flowType = selectedCategory?.flow_type || "normal";

  const tableHeaders = React.useMemo(() => {
    const commonHeadersBefore = [
      {
        prop: "product_name",
        title: "Product Name",
        isSortable: true,
        isFilterable: true,
        cell: (row) => (
          <Link
            to={`/product/details/${row.id}`}
            className="text-decoration-none fw-bold text-primary"
            style={{ cursor: "pointer" }}
          >
            {row.product_name}
          </Link>
        ),
      },
      {
        prop: "image_url",
        title: "Images",
        cell: (row) => {
          const imageUrls = row.image_url || [];
          const parsedUrls = Array.isArray(imageUrls) ? imageUrls : (typeof imageUrls === 'string' ? JSON.parse(imageUrls) : []);
          const firstImage = parsedUrls[0] || null;
          const imageCount = parsedUrls.length;

          return (
            <div className="product-image" style={{ position: "relative" }}>
              {firstImage ? (
                <>
                  <img
                    src={firstImage || process.env.PUBLIC_URL + "/not-img-found.png"}
                    alt={row.product_name}
                    style={{ width: "50px", height: "50px", objectFit: "cover", borderRadius: "4px" }}
                    onError={(e) => { e.target.onerror = null; e.target.src = process.env.PUBLIC_URL + "/not-img-found.png"; }}
                  />
                  {imageCount > 1 && (
                    <div style={{ position: "absolute", top: "-5px", right: "-5px", backgroundColor: "#007bff", color: "white", borderRadius: "50%", width: "18px", height: "18px", fontSize: "10px", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "bold" }} title={`${imageCount} images`}>
                      +{imageCount - 1}
                    </div>
                  )}
                </>
              ) : (
                <div style={{ width: "50px", height: "50px", backgroundColor: "#f0f0f0", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "4px", fontSize: "12px", color: "#666" }}>No Image</div>
              )}
            </div>
          );
        },
      },
    ];

    const flowSpecificHeaders = [];
    if (flowType === "school") {
      flowSpecificHeaders.push(
        { prop: "board_name", title: "Board", isSortable: true, isFilterable: true, cell: (row) => <DynamicBadge text={row.board_name || "N/A"} color="#6366f1" /> },
        { prop: "class_name", title: "Class", isSortable: true, isFilterable: true, cell: (row) => <DynamicBadge text={row.class_name || "N/A"} color="#8b5cf6" /> },
        { prop: "subject_name", title: "Subject", isSortable: true, isFilterable: true, cell: (row) => <DynamicBadge text={row.subject_name || "N/A"} color="#ec4899" /> }
      );
    } else if (flowType === "college") {
      flowSpecificHeaders.push(
        { prop: "university_name", title: "University", isSortable: true, isFilterable: true, cell: (row) => <DynamicBadge text={row.university_name || "N/A"} color="#0ea5e9" /> },
        { prop: "course_name", title: "Course", isSortable: true, isFilterable: true, cell: (row) => <DynamicBadge text={row.course_name || "N/A"} color="#06b6d4" /> },
        { prop: "semester_name", title: "Sem", isSortable: true, isFilterable: true, cell: (row) => <DynamicBadge text={row.semester_name || "N/A"} color="#14b8a6" /> },
        { prop: "subject_name", title: "Subject", isSortable: true, isFilterable: true, cell: (row) => <DynamicBadge text={row.subject_name || "N/A"} color="#ec4899" /> }
      );
    } else if (flowType === "competitive") {
      flowSpecificHeaders.push(
        { prop: "exam_category_name", title: "Exam Category", isSortable: true, isFilterable: true, cell: (row) => <DynamicBadge text={row.exam_category_name || "N/A"} color="#f59e0b" /> },
        { prop: "exam_name", title: "Exam", isSortable: true, isFilterable: true, cell: (row) => <DynamicBadge text={row.exam_name || "N/A"} color="#ef4444" /> },
        { prop: "competitive_subject_name", title: "Subject", isSortable: true, isFilterable: true, cell: (row) => <DynamicBadge text={row.competitive_subject_name || "N/A"} color="#ec4899" /> }
      );
    }

    const commonHeadersAfter = [
      {
        prop: "category_name",
        title: "Category",
        isSortable: true,
        isFilterable: true,
        cell: (row) => <DynamicBadge text={row.category_name || "N/A"} />,
      },
      {
        prop: "selling_price",
        title: "Price",
        isFilterable: true,
        isSortable: true,
        cell: (row) => {
          const mrp = parseFloat(row.mrp || 0);
          const sellingPrice = parseFloat(row.selling_price || 0);
          const discount = mrp - sellingPrice;
          return (
            <div>
              {discount > 0 ? (
                <div>
                  <div className="text-success">₹{sellingPrice.toFixed(2)}</div>
                  <div className="small text-muted"><s>₹{mrp.toFixed(2)}</s></div>
                </div>
              ) : (
                <div>₹{sellingPrice.toFixed(2)}</div>
              )}
            </div>
          );
        },
      },
      {
        prop: "stock_quantity",
        title: "Stock",
        isSortable: true,
        isFilterable: true,
        cell: (row) => (
          <Badge bg={row.stock_quantity > 10 ? "success" : row.stock_quantity > 0 ? "warning" : "danger"}>
            {row.stock_quantity}
          </Badge>
        ),
      },
      {
        prop: "product_status",
        title: "Status",
        isSortable: true,
        isFilterable: true,
        cell: (row) => {
          let badgeColor = "secondary";
          if (row.product_status === "PUBLISHED") badgeColor = "success";
          else if (row.product_status === "HOLD") badgeColor = "warning";
          else if (row.product_status === "UNLISTED") badgeColor = "danger";
          return <Badge bg={badgeColor}>{row.product_status || 'UNLISTED'}</Badge>;
        },
      },
      ...(user?.user_type === "admin" || user?.user_type === "super_admin"
        ? [
          {
            prop: "vendor_name",
            title: "Vendor",
            isSortable: true,
            isFilterable: true,
            cell: (row) => <DynamicBadge text={row.vendor_name || "Admin"} />
          }
        ]
        : []),
      {
        prop: "actions",
        title: "Actions",
        cell: (row) => (
          <div className="d-flex gap-2">
            <Button size="sm" variant="outline-warning" onClick={() => handleEditProduct(row.id)}><FaPencilAlt /></Button>
            <Button size="sm" variant="outline-danger" onClick={() => handleDeleteProduct(row.id, row.product_name)} disabled={isDeleting}><FaRegTrashAlt /></Button>
          </div>
        ),
      }
    ];

    return [...commonHeadersBefore, ...flowSpecificHeaders, ...commonHeadersAfter];
  }, [flowType, user, isDeleting]);

  // Only show full page loading on initial load when there are no products
  if (isLoading && isInitialLoad && products.length === 0) {
    return <div className="text-center mt-4">Loading products...</div>;
  }

  // Logic to calculate filtered counts for stats cards
  const filteredProducts = products.filter(p => {
    let matches = true;
    if (categoryFilter && p.category_id !== categoryFilter) matches = false;
    if (subCategoryFilter && p.sub_category_id !== subCategoryFilter) matches = false;
    if (vendorFilter && p.createdbyid !== vendorFilter) matches = false;
    return matches;
  });

  const stats = {
    totalProducts: filteredProducts.length,
    totalCategories: new Set(filteredProducts.map(p => p.category_id).filter(Boolean)).size,
    totalSubCategories: new Set(filteredProducts.map(p => p.sub_category_id).filter(Boolean)).size,
    totalVendors: new Set(filteredProducts.map(p => p.createdbyid).filter(Boolean)).size
  };

  const labels = {
    beforeSelect: "Items per page",
  };

  return (
    <div className="page-container">
      {/* Breadcrumb */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <Link className="breadcrum mb-0" to="/">
          Home{"/"}
          <i
            className="fa-solid fa-chevron-right"
            style={{ marginLeft: "1px", marginRight: "1px" }}
          ></i>{" "}
          Products
        </Link>
        <div className="d-flex gap-2">
          <Button
            onClick={() => setShowBulkUpload(true)}
            variant="warning"
            className="btn-sm px-4 fw-bold shadow-sm d-flex align-items-center"
            style={{ color: '#000', border: 'none' }}
          >
            <FaCloudUploadAlt className="me-2" /> Bulk Upload
          </Button>
        </div>
      </div>

      {/* Admin Stats Dashboard */}
      {(user?.user_type === "super_admin" || user?.user_type === "admin") && (
        <div className="row g-3 mb-4">
          <div className="col-md-3">
            <div className="admin-stats-card p-3 shadow-sm border rounded bg-white d-flex align-items-center">
              <div className="stats-icon-wrapper bg-primary-soft text-primary me-3">
                <FaBox size={20} />
              </div>
              <div>
                <div className="stats-label text-muted small fw-semibold">Total Products</div>
                <div className="stats-value h4 mb-0 fw-bold">{stats.totalProducts}</div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="admin-stats-card p-3 shadow-sm border rounded bg-white d-flex align-items-center">
              <div className="stats-icon-wrapper bg-success-soft text-success me-3">
                <FaLayerGroup size={20} />
              </div>
              <div>
                <div className="stats-label text-muted small fw-semibold">Categories</div>
                <div className="stats-value h4 mb-0 fw-bold">{stats.totalCategories}</div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="admin-stats-card p-3 shadow-sm border rounded bg-white d-flex align-items-center">
              <div className="stats-icon-wrapper bg-info-soft text-info me-3">
                <FaSitemap size={20} />
              </div>
              <div>
                <div className="stats-label text-muted small fw-semibold">Sub Categories</div>
                <div className="stats-value h4 mb-0 fw-bold">{stats.totalSubCategories}</div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="admin-stats-card p-3 shadow-sm border rounded bg-white d-flex align-items-center">
              <div className="stats-icon-wrapper bg-warning-soft text-warning me-3">
                <FaUsers size={20} />
              </div>
              <div>
                <div className="stats-label text-muted small fw-semibold">Total Vendors</div>
                <div className="stats-value h4 mb-0 fw-bold">{stats.totalVendors}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        show={showConfirmModal}
        onHide={cancelDeleteProduct}
        onConfirm={confirmDeleteProduct}
        title="Delete Product"
        message={
          productToDelete?.isBulk
            ? `Are you sure you want to delete ${productToDelete.name}? This action cannot be undone.`
            : `Are you sure you want to delete the product "${productToDelete?.name}"? This action cannot be undone.`
        }
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={isDeleting}
      />

      {/* Bulk Delete Button */}
      {selectedProducts.length > 0 && (
        <div className="mb-3">
          <Button
            variant="danger"
            onClick={handleBulkDelete}
            disabled={isDeleting}
            className="btn-sm"
          >
            Delete Selected ({selectedProducts.length})
          </Button>
        </div>
      )}

      <DatatableWrapper
        body={products || []}
        headers={tableHeaders || []}
        sortProps={{
          initialState: {
            prop: "createddate",
            order: "descending",
          },
        }}
        paginationOptionsProps={{
          initialState: { rowsPerPage: 5, options: [5, 10, 15, 20] },
        }}
      >
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3 p-3 bg-light rounded border">
          <div className="d-flex flex-wrap align-items-center gap-2 flex-grow-1">
            {/* Search Filter */}
            <div style={{ minWidth: "250px" }}>
              <Filter
                placeholder="Search products..."
                className="form-control-sm"
              />
            </div>

            {/* Category Filter */}
            <div style={{ minWidth: "160px" }}>
              <Form.Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                size="sm"
                className="form-select-sm shadow-none"
              >
                <option value="">All Categories</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </Form.Select>
            </div>

            {/* Sub Category Filter */}
            <div style={{ minWidth: "160px" }}>
              <Form.Select
                value={subCategoryFilter}
                onChange={(e) => setSubCategoryFilter(e.target.value)}
                size="sm"
                className="form-select-sm shadow-none"
                disabled={!categoryFilter}
              >
                <option value="">All Subcategories</option>
                {Array.isArray(subCategories) && subCategories.map((sub) => (
                  <option key={sub.id} value={sub.id}>
                    {sub.name}
                  </option>
                ))}
              </Form.Select>
            </div>

            {/* Vendor Filter (Super Admin/Admin Only) */}
            {(user?.user_type === "super_admin" || user?.user_type === "admin") && (
              <div style={{ minWidth: "160px" }}>
                <Form.Select
                  value={vendorFilter}
                  onChange={(e) => setVendorFilter(e.target.value)}
                  size="sm"
                  className="form-select-sm shadow-none"
                >
                  <option value="">All Vendors</option>
                  {Array.isArray(vendors) && vendors.map((vendor) => (
                    <option key={vendor.id} value={vendor.id}>
                      {vendor.first_name} {vendor.last_name}
                    </option>
                  ))}
                </Form.Select>
              </div>
            )}
          </div>

          <div className="d-flex align-items-center gap-3">
            <div className="d-flex align-items-center">
              <PaginationOptions labels={labels} />
            </div>
            <div className="d-none d-lg-block">
              <InfoPill left="Total" right={products?.length} />
            </div>
            <Button
              onClick={handleCreateProduct}
              variant="dark"
              className="btn-sm px-3 fw-bold"
              disabled={isLoading}
            >
              New Product
            </Button>
          </div>
        </div>

        {/* Table */}
        <div
          className="table-responsive shadow-sm"
          style={{ position: "relative" }}
        >
          {isLoading && isInitialLoad && products.length === 0 && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
                borderRadius: "4px",
              }}
            >
              <Spinner animation="border" variant="success" />
            </div>
          )}
          <table className="table table-striped table-bordered align-middle">
            <TableHeader />
            <TableBody />
          </table>
        </div>

        {/* Pagination */}
        <div className="compact-pagination d-flex justify-content-left mt-3">
          <Pagination classes={{
            button: "bg-dark",
          }} />
        </div>
      </DatatableWrapper>

      {/* Bulk Upload Modal */}
      <BulkUploadModal
        show={showBulkUpload}
        onHide={() => setShowBulkUpload(false)}
        categories={categories}
        onSuccess={() => {
          // Refetch products on success
          const params = {};
          if (categoryFilter) params.category_id = categoryFilter;
          if (subCategoryFilter) params.sub_category_id = subCategoryFilter;
          if (vendorFilter) params.vendor_id = vendorFilter;
          dispatch(fetchProducts(params));
        }}
      />
    </div>
  );
};

export default Products;
